import React, { useEffect, useRef, useState, useTransition } from 'react';
import { clickEndsPress, isPlainMousePress } from '../hooks/usePressAction';

/** What `press(value)` returns: spread it on the option's button. */
export interface PickHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onClick: (e: React.MouseEvent) => void;
}

/**
 * [FLUID-8 2026-10-12, user] "just like you added this thing to dropdown, the fastness I want the same to the complete
 * application". A view pill or a row of filter chips whose choice lives in a big page used to light up only after the
 * WHOLE page had re-rendered for the new choice (the Dialing tables, the My Sales board, the people list…), so it lagged
 * the click — and the click itself only fires when the mouse button comes back up, ~0.1 s in.
 *
 * InstantPick holds the choice just made itself: the pill / chip lights up on the mouse PRESS (touch and pen keep the
 * click — a press there may be a scroll; the keyboard's Enter / Space click), and the page's own switch follows as a
 * low-priority update (a transition) that can never hold the pill back. A second pick while the page is still catching
 * up is ignored, so the pill and the page never disagree. Once the switch has landed — or the page's value moved on by
 * itself (a search snapping the chips back to All) — the pill shows the page's value again, so it can never stick on a
 * pick the page did not take.
 *
 * Render-prop: `children(shown, press)` draws the page's own markup — light up by `shown`, spread `press(v)` on each
 * option. Only this small component re-renders on the pick; the page re-renders once, in the transition.
 */
export function InstantPick<T>({ value, onPick, children }: {
  value: T;
  onPick: (v: T) => void;
  children: (shown: T, press: (v: T) => PickHandlers) => React.ReactNode;
}) {
  const [picked, setPicked] = useState<{ v: T } | null>(null);
  const [pending, startPick] = useTransition();
  useEffect(() => { if (!pending) setPicked(null); }, [pending, value]);
  const shown = picked ? picked.v : value;
  const pressed = useRef(false);
  const pick = (v: T) => {
    if (Object.is(v, shown) || pending) return;
    setPicked({ v });
    startPick(() => onPick(v));
  };
  const press = (v: T): PickHandlers => ({
    onPointerDown: (e) => {
      pressed.current = isPlainMousePress(e);
      if (pressed.current) pick(v);
    },
    onClick: (e) => {
      if (clickEndsPress(pressed, e)) return; // the press already did it
      pick(v);
    },
  });
  return <>{children(shown, press)}</>;
}

export default InstantPick;
